import { Component, inject, OnDestroy, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ConfigService, TranslatePipe } from '@cmi/addin-core';

@Component({
  selector: 'cmi-settings',
  imports: [RouterLink, TranslatePipe],
  templateUrl: './settings.html'
})
export class Settings implements OnDestroy {
  private readonly _configService = inject(ConfigService);
  private _copyFeedbackTimeoutId: ReturnType<typeof setTimeout> | null = null;

  protected readonly buildVersion = this._configService.config.buildVersion;
  protected readonly copyFeedback = signal<'copied' | 'failed' | null>(null);

  protected copyBuildVersion(): void {
    if (!this.buildVersion) {
      return;
    }

    void this._copyToClipboard(this.buildVersion);
  }

  private async _copyToClipboard(value: string): Promise<void> {
    const didCopyWithNavigator = await this._copyWithNavigatorClipboard(value);
    const didCopy = didCopyWithNavigator || this._copyWithExecCommand(value);

    this.copyFeedback.set(didCopy ? 'copied' : 'failed');
    this._scheduleCopyFeedbackReset();
  }

  private async _copyWithNavigatorClipboard(value: string): Promise<boolean> {
    const clipboard = globalThis.navigator?.clipboard;
    if (!clipboard?.writeText) {
      return false;
    }

    try {
      await clipboard.writeText(value);
      return true;
    } catch {
      return false;
    }
  }

  private _copyWithExecCommand(value: string): boolean {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';

    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    let didCopy = false;
    try {
      didCopy = document.execCommand('copy');
    } catch {
      didCopy = false;
    }

    document.body.removeChild(textarea);
    return didCopy;
  }

  private _scheduleCopyFeedbackReset(): void {
    if (this._copyFeedbackTimeoutId !== null) {
      clearTimeout(this._copyFeedbackTimeoutId);
    }

    this._copyFeedbackTimeoutId = setTimeout(() => {
      this.copyFeedback.set(null);
      this._copyFeedbackTimeoutId = null;
    }, 2000);
  }

  public ngOnDestroy(): void {
    if (this._copyFeedbackTimeoutId !== null) {
      clearTimeout(this._copyFeedbackTimeoutId);
      this._copyFeedbackTimeoutId = null;
    }
  }
}
